import React from 'react'
import { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import WT from '@sscale/wtsdk';
import moment from 'moment'
import './Session.css'

import { vrtApiRequest } from '../../httpClient';
import { initializeSyncSdk } from '../../services/syncService'

import { resetStore as resetParticipantStore, setGainedToken} from '../../store/reducers/ParticipantReducer';
import {
  setSessionError, setJoinModalState, setSurveyModalState,
  setRoomProcessingModalState, setRoomCheckerError, setTimeBeforeMeeting,
  setJoinModalType, setParticipants,
} from '../../store/reducers/SessionReducer';

import Video from '../Video/Video';
import JoinModal from '../JoinModal/JoinModal'
import CelebrityPopup from '../CelebrityPopup/CelebrityPopup'
import RoomCheckerModal from '../RoomCheckerModal/RoomCheckerModal'
import CelebrityTable from './components/CelebrityTable/CelebrityTable'
import FooterButtons from './components/FooterButtons/FooterButtons'
import Player from '../Player/Player'
import SurveyModal from '../SurveyModal/SurveyModal';
import SessionError from './components/SessionError/SessionError';
import ParticipantsList from './components/ParticipantsList/ParticipantsList'

function Session() {
  const { roomId = '' } = useParams();

  const dispatch = useDispatch()

  const { isCelebrity, token, userName } = useSelector(state => state.participant);
  const { participants } = useSelector(state => state.session);

  const [isStreamCreated, setStreamCreateState] = useState(false)
  const [roomHasCelebrity, setRoomCelebrityPresence] = useState(false);
  const [roomsList, setRoomsList] = useState([])

  const participantsRef = useRef(participants);
  const roomHasCelebrityRef = useRef(roomHasCelebrity);
  const isCelebrityRef = useRef(isCelebrity);
  const intervalIdRef = useRef(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if( /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ) {
      setIsMobile(true)
    }

  }, [window.clientWidth]);

  useEffect(() => {
    isCelebrityRef.current = isCelebrity;
  }, [isCelebrity])

  useEffect(() => {
    if (isCelebrityRef.current) {
      setStreamCreateState(true)
      intervalIdRef.current = setInterval(() => getRoomsList(), 3000)
    }

    return () => {
      clearInterval(intervalIdRef.current)
    }
  }, [isCelebrityRef.current])

  useEffect(() => {
    initializeSyncSdk()
    processRoom()
  }, [roomId])

  useEffect(() => {
    if (isCelebrity !== null && userName) {
      if (isCelebrity) {
        if (roomHasCelebrity) {
          return
        }

        makeCelebrityLocal()

      } else {
        if (token && userName) {
          WT.Session.connect(token, userName, {}, { isCelebrity: isCelebrity })
        }
      }
    }
  }, [token, userName, isCelebrity]);

  useEffect(() => {
    participantsRef.current = participants;
  }, [participants]);

  useEffect(() => {
    roomHasCelebrityRef.current = roomHasCelebrity;
  }, [roomHasCelebrity]);

  useEffect(() => {
    WT.ErrorsListeners.onSessionError((error) => {
      if (error.code === 206) {
        dispatch(setSessionError('Unable to join the room. Exceeded limit of streaming participants'))

        dispatch(resetParticipantStore())
        stopLocalStream()
        dispatch(setParticipants([]))
      }
    })

    let participants = [];

    WT.SessionListeners.onConnected((params) => {
      if (!params.length) {
        dispatch(setJoinModalState(true))
      }

      WT.SessionListeners.onStreamCreated((params) => {
        setStreamCreateState(true);

        const alreadyExists = participants.find(p => p.participantId === params.participantId);

        if (alreadyExists) {
          const video = document.getElementById('participant-' + params.participantId);
          video.src = params.stream;
          // dispatch(setParticipants([...participants.filter(p => p.participantId !== params.participantId), { ...params }]))
        } else if (params.isCelebrity) {
          setRoomCelebrityPresence(params)
        } else {
          participants.push(params)
          dispatch(setParticipants([...participants]))
        }
      });

      WT.ParticipantListeners.onParticipantLeft(({ participantId }) => {
        if (roomHasCelebrityRef?.current && roomHasCelebrityRef?.current?.participantId === participantId) {
          setRoomCelebrityPresence(null)
        } else {
          participants = [...participants.filter(p => p.participantId !== participantId)];
          dispatch(setParticipants(participants.filter(p => p.participantId !== participantId)))
        }
      })
    })
  }, []);

  const makeCelebrityLocal = () => {
    dispatch(setParticipants([]))
    const constraints = {
      'video': true,
      'audio': false
    }
    navigator.mediaDevices.getUserMedia(constraints).then(device => (
        setRoomCelebrityPresence({ stream: device, participantId: '1' })
    ))
  }

  const stopLocalStream = () => {
    const tracks = roomHasCelebrityRef?.current?.stream?.getTracks()

    if (tracks && tracks.length > 0) {
      tracks[0].stop()
    }
  }

  useEffect(() => {
    return () => {
      stopLocalStream()
    }
  }, [])

  const processRoom = async () => {
    if (roomId && roomId !== 'c') {
      dispatch(setRoomProcessingModalState(true))

      try {
        const res = await vrtApiRequest.get(`/room/${roomId}`);

        dispatch(setJoinModalType('USER_DATA_ENTRY'))

        if (res.data.token) {
          dispatch(setGainedToken(res?.data))

          dispatch(setRoomProcessingModalState(false))
        } else {
          dispatch(setTimeBeforeMeeting(moment.duration(res?.data?.toStart, 'minutes').humanize()))
        }

      } catch (error) {
        if (error.message.includes('400')) {
          dispatch(setRoomCheckerError('Room does not exist. Please try to use another link.'))
          return
        }
        dispatch(setRoomCheckerError('Unexpected error'))
      }
    }
  }

  const getRoomsList = async () => {

    const res = await vrtApiRequest.get('/room/get-available-rooms-for-celebrity');

    setRoomsList(res?.data || [])
  }

  function leaveCall() {
    WT.Session.disconnect();

    stopLocalStream()

    dispatch(setJoinModalState(false))
    setRoomCelebrityPresence(null)
    dispatch(setParticipants([]))
    dispatch(resetParticipantStore())
    dispatch(setSurveyModalState(true))

    dispatch(setJoinModalType('BUTTONS'))
  }

  return (
      <div className='session-container'>
        <div className='s-header-container'>
          <div className='s-header-container'>
            <div className='s-logo'>
            </div>
          </div>
        </div>

        {roomHasCelebrity && (
            <div className='s-participants-container right_margin'>
              <div className='s-participants-empty-space' />

              <div className='s-participant-video-block-wrapper'>
                <div className='s-participant-video-block celebrity'>
                  <Video participant={roomHasCelebrity} />
                </div>
              </div>

              {isCelebrity && (
                  <FooterButtons leaveCall={leaveCall} />
              )}
            </div>
        )}

        {(isStreamCreated || !isMobile) && (<div className='s-activity-container'>
          <div className='s-participants-empty-space' />

          <div className={`s-player-container ${!token ? 'placeholder' : ''} ${isCelebrity ? 'celebrity' : ''}`}>
            {isStreamCreated && (token || isCelebrity) ? (
                <Player isCelebrity={isCelebrity} />
            ) : (
                <div className='s-player-button-placeholder' />
            )}
          </div>

          <div className={`s-session-footer ${isCelebrity ? 'celebrity' : ''}`}>
            <CelebrityTable
                visible={isCelebrity}
                data={roomsList}
                makeCelebrityLocal={makeCelebrityLocal}
            />

            {!isCelebrity && (
                <FooterButtons leaveCall={leaveCall} />
            )}
          </div>
        </div>)}

        <div className='s-participants-container left_marging'>
          <div className='s-participants-empty-space' />

          <ParticipantsList />
        </div>

        <JoinModal />

        <RoomCheckerModal />

        <CelebrityPopup />

        <SessionError />

        <SurveyModal />
      </div >
  )
}

export default Session;
